import type { TaskComment } from '@/src/renderer/lib/db/models'

/** Was die Liste von einem Kommentar braucht — an einer Aufgabe wie an einem Projekt (#184). */
export type CommentEntry = Pick<TaskComment, 'id' | 'content' | 'postedAt'>
import { addDays, dateKey, isSameDay } from '@/src/renderer/lib/date/calendar'

/** Kommentare einer Person am Stück — eine Autoren-Pille darüber, wie bei WhatsApp (#183). */
export interface CommentRun {
  /** Kennung des ersten Kommentars — bleibt, wenn unten einer dazukommt. */
  key: string
  comments: CommentEntry[]
}

/** Die Kommentare eines Tages, mit seiner Überschrift, in Folgen je Person. */
export interface CommentDay {
  /** `YYYY-MM-DD` in lokaler Zeit. */
  key: string
  label: string
  runs: CommentRun[]
}

/**
 * **Wer einen Kommentar geschrieben hat.** Bis es Freigaben gibt, ist jeder der eigene — wie an
 * der Memo (`MemoAuthor`). Nicht `userId`: Die steht erst nach dem ersten Sync da, und eine Folge
 * zerfiele sonst an der Grenze zwischen hochgeladenen und frischen Kommentaren.
 */
export function commentAuthor(_comment: CommentEntry): string {
  return 'self'
}

/**
 * **Nach Tagen gruppiert**, in lokaler Zeit (#183): „Heute", „Gestern", sonst Wochentag und
 * Datum — das Jahr nur, wenn es nicht das laufende ist. Darin **Folgen**: Was eine Person am Stück
 * geschrieben hat, steht unter einer Autoren-Pille. Die Reihenfolge der Kommentare bleibt, wie sie
 * hereinkommt (die Quelle liefert sie nach `postedAt`, der älteste zuerst).
 */
export function groupCommentsByDay(
  comments: readonly CommentEntry[],
  now: Date,
  locale: string,
  words: { today: string; yesterday: string },
): CommentDay[] {
  const days: CommentDay[] = []
  for (const comment of comments) {
    const key = dateKey(comment.postedAt)
    let day = days[days.length - 1]
    if (day?.key !== key) {
      day = { key, label: dayLabel(comment.postedAt, now, locale, words), runs: [] }
      days.push(day)
    }
    const run = day.runs[day.runs.length - 1]
    if (run && commentAuthor(run.comments[0]!) === commentAuthor(comment)) run.comments.push(comment)
    else day.runs.push({ key: comment.id, comments: [comment] })
  }
  return days
}

function dayLabel(day: Date, now: Date, locale: string, words: { today: string; yesterday: string }): string {
  if (isSameDay(day, now)) return words.today
  if (isSameDay(day, addDays(now, -1))) return words.yesterday
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(day.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(day)
}

/** Die Uhrzeit über der Blase, in der Schreibweise der Sprache (`14:05`, `2:05 PM`). */
export function commentTime(at: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(at)
}
